/**
 * Controles (itens 4.1, 4.2 e 4.4), como na tabela da seção 5 do GDD.
 * Teclado e mouse: WASD voa, Espaço sobe, Shift desce, Q turbo, clique esquerdo (ou F) é a ação, clique direito
 * pede a bola, E é a habilidade e 1–4 são o chat rápido.
 * Gamepad: analógico esquerdo, RT sobe, LT desce, LB turbo, A ação, segurar X carrega o arremesso, B pede a bola,
 * Y é a habilidade e o direcional é o chat rápido.
 * Com a Pelota na mão, toque curto passa e segurar carrega o arremesso (solta para arremessar).
 * O direcional é relativo à câmera; a simulação recebe a direção já no mundo.
 */
import { BUTTON, quantizeInput, type InputCommand } from "@besenliga/sim";

/** Intenção do jogador, antes de considerar a câmera. Eixos de -1 a 1. */
export interface Intent {
  forward: number;
  right: number;
  up: number;
  turbo: boolean;
  /** Ação principal neste tick (passe, rebatida, defesa, agarrar). */
  action?: boolean;
  /** Carregando o arremesso. */
  charge?: boolean;
  /** Habilidade da posição (a simulação dispara na borda). */
  ability?: boolean;
  /** Pedindo a bola. */
  call?: boolean;
  /** Frase do chat rápido escolhida neste tick (0 a 3). */
  chat?: number;
}

/** Frases do chat rápido (teclas 1–4 ou direcional: cima, direita, baixo, esquerda). */
export const QUICK_CHAT = ["Passa!", "Atrás de você!", "Volta!", "Boa!"] as const;

/** Segurar a ação por mais que isto (s) vira arremesso carregado em vez de passe. */
export const HOLD_SECONDS = 0.22;

export const GAMEPAD_DEADZONE = 0.15;

/**
 * Converte a intenção em comando no mundo. `camForward` é a direção da câmera no chão (x, z), unitária.
 * A direita da câmera é (-z, x) porque o eixo y aponta para cima.
 */
export function commandFrom(intent: Intent, camForward: { x: number; z: number }): InputCommand {
  const rx = -camForward.z;
  const rz = camForward.x;
  let x = camForward.x * intent.forward + rx * intent.right;
  let z = camForward.z * intent.forward + rz * intent.right;
  const len = Math.sqrt(x * x + z * z);
  if (len > 1) {
    x /= len;
    z /= len;
  }
  const buttons =
    (intent.turbo ? BUTTON.turbo : 0) |
    (intent.action ? BUTTON.action : 0) |
    (intent.charge ? BUTTON.charge : 0) |
    (intent.ability ? BUTTON.ability : 0) |
    (intent.call ? BUTTON.call : 0);
  return quantizeInput(x, intent.up, z, buttons);
}

const dead = (v: number) => (Math.abs(v) < GAMEPAD_DEADZONE ? 0 : v);

/** Lê teclado e o primeiro gamepad conectado. */
export class Controls {
  private readonly keys = new Set<string>();
  private actionHeld = false;
  private actionDownAt = 0;
  private actionPressed = false;
  private tapPending = false;
  private padA = false;
  private abilityLatched = false;
  private callHeld = false;
  private chat: number | null = null;
  private padDpad = -1;

  constructor(
    private readonly target: Window = window,
    private readonly surface: HTMLElement | null = null,
  ) {
    target.addEventListener("keydown", this.onDown);
    target.addEventListener("keyup", this.onUp);
    target.addEventListener("blur", this.onBlur);
    surface?.addEventListener("pointerdown", this.onPointerDown);
    surface?.addEventListener("contextmenu", this.onContextMenu);
    target.addEventListener("pointerup", this.onPointerUp);
  }

  /**
   * Lê os controles para um tick. `tapToAct`: com a Pelota na mão, o toque curto é passe (sai ao soltar) e
   * segurar carrega o arremesso; sem ela, a ação sai no aperto.
   */
  intent(tapToAct = false): Intent {
    const now = performance.now() / 1000;
    const k = (code: string) => (this.keys.has(code) ? 1 : 0);
    const intent: Intent = {
      forward: k("KeyW") - k("KeyS"),
      right: k("KeyD") - k("KeyA"),
      up: k("Space") - (k("ShiftLeft") || k("ShiftRight")),
      turbo: this.keys.has("KeyQ"),
      ability: this.abilityLatched || this.keys.has("KeyE"),
      call: this.callHeld,
    };
    this.abilityLatched = false;
    const pad = typeof navigator.getGamepads === "function" ? navigator.getGamepads()[0] : null;
    if (pad) {
      const lx = dead(pad.axes[0] ?? 0);
      const ly = dead(pad.axes[1] ?? 0);
      if (lx || ly) {
        intent.right = lx;
        intent.forward = -ly;
      }
      const up = (pad.buttons[7]?.value ?? 0) - (pad.buttons[6]?.value ?? 0);
      if (Math.abs(up) > GAMEPAD_DEADZONE) intent.up = up;
      intent.turbo ||= pad.buttons[4]?.pressed ?? false;
      const a = pad.buttons[0]?.pressed ?? false;
      if (a && !this.padA) this.actionPressed = true;
      this.padA = a;
      if (pad.buttons[2]?.pressed) intent.charge = true;
      if (pad.buttons[1]?.pressed) intent.call = true;
      if (pad.buttons[3]?.pressed) intent.ability = true;
      // Direcional: 12 cima, 15 direita, 13 baixo, 14 esquerda.
      const dpad = [12, 15, 13, 14].findIndex((b) => pad.buttons[b]?.pressed);
      if (dpad >= 0 && dpad !== this.padDpad) this.chat = dpad;
      this.padDpad = dpad;
    }
    if (this.chat !== null) {
      intent.chat = this.chat;
      this.chat = null;
    }
    if (tapToAct) {
      intent.action = this.tapPending;
      intent.charge ||= this.actionHeld && now - this.actionDownAt >= HOLD_SECONDS;
      this.tapPending = false;
      // Sem soltar, o aperto do gamepad A também passa.
      if (this.actionPressed && !this.actionHeld) intent.action = true;
    } else {
      intent.action = this.actionPressed;
      this.tapPending = false;
    }
    this.actionPressed = false;
    return intent;
  }

  dispose(): void {
    this.target.removeEventListener("keydown", this.onDown);
    this.target.removeEventListener("keyup", this.onUp);
    this.target.removeEventListener("blur", this.onBlur);
    this.surface?.removeEventListener("pointerdown", this.onPointerDown);
    this.surface?.removeEventListener("contextmenu", this.onContextMenu);
    this.target.removeEventListener("pointerup", this.onPointerUp);
  }

  private actionDown(): void {
    if (this.actionHeld) return;
    this.actionHeld = true;
    this.actionPressed = true;
    this.actionDownAt = performance.now() / 1000;
  }

  private actionUp(): void {
    if (!this.actionHeld) return;
    this.actionHeld = false;
    if (performance.now() / 1000 - this.actionDownAt < HOLD_SECONDS) this.tapPending = true;
  }

  private readonly onPointerDown = (e: PointerEvent) => {
    if (e.button === 0) this.actionDown();
    if (e.button === 2) this.callHeld = true;
  };

  private readonly onContextMenu = (e: Event) => e.preventDefault();

  private readonly onPointerUp = (e: PointerEvent) => {
    if (e.button === 0) this.actionUp();
    if (e.button === 2) this.callHeld = false;
  };

  private readonly onDown = (e: KeyboardEvent) => {
    if (e.code === "KeyF" && !e.repeat) this.actionDown();
    if (e.code === "KeyE" && !e.repeat) this.abilityLatched = true;
    const chat = ["Digit1", "Digit2", "Digit3", "Digit4"].indexOf(e.code);
    if (chat >= 0 && !e.repeat) this.chat = chat;
    if (
      ["KeyW", "KeyA", "KeyS", "KeyD", "Space", "ShiftLeft", "ShiftRight", "KeyQ", "KeyE"].includes(e.code)
    ) {
      this.keys.add(e.code);
      if (e.code === "Space") e.preventDefault();
    }
  };

  private readonly onUp = (e: KeyboardEvent) => {
    if (e.code === "KeyF") this.actionUp();
    this.keys.delete(e.code);
  };

  private readonly onBlur = () => {
    this.keys.clear();
    this.actionHeld = false;
    this.callHeld = false;
  };
}
